import {queryOptions, useMutation, useQueryClient} from '@tanstack/react-query';
import {api, apiJson, ApiError} from './client';
import {customResumableEndpoint} from './uploadResumable';
import {useResumableUploadQueue, type ResumableQueueApi} from './useResumableUploadQueue';

// API контракт відповідає `getCustomRequestForUser` у бекенді
// (apps/api/src/lib/customRequests.ts) + DriveListedFile з drive.ts.
export interface CustomRequestFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
}

export type CustomRequestState = 'uploading' | 'submitted' | 'published';

export interface CustomRequestView {
  title: string;
  description: string;
  state: CustomRequestState;
  files: CustomRequestFile[];
  // Включён ли перенос файлов по ссылке на Google Диск (LINK_IMPORT_ENABLED на сервере).
  linkImport: boolean;
}

export interface SubmitCustomRequestResponse {
  ok: true;
  webViewLink: string | null;
}

// Token передаємо у `Authorization: Bearer` хедері — щоб не світити у access-логах
// як query-параметр (бекенд приймає обидва способи, див. requireCustomRequestToken).
function bearer(token: string): HeadersInit {
  return {Authorization: `Bearer ${token}`};
}

export function customRequestQueryOptions(id: string, token: string) {
  return queryOptions({
    queryKey: ['custom-request', id],
    queryFn: () =>
      api<CustomRequestView>(`/v1/custom-requests/${encodeURIComponent(id)}`, {
        headers: bearer(token),
      }),
    retry: (failureCount, err) => {
      // 403/404 — токен/ID невалідні, не ретраїмо.
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return false;
      return failureCount < 1;
    },
  });
}

export function useSubmitCustomRequest(id: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<SubmitCustomRequestResponse>(
        `/v1/custom-requests/${encodeURIComponent(id)}/submit`,
        {},
        {headers: bearer(token)},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: ['custom-request', id]});
    },
  });
}

// Тонкий wrapper над спільним useResumableUploadQueue з прив'язкою до
// custom-request ендпойнта і Bearer-токеном у заголовках.
export function useCustomUploadQueue(id: string, token: string): ResumableQueueApi {
  const qc = useQueryClient();
  return useResumableUploadQueue({
    endpoint: customResumableEndpoint(id),
    extraHeaders: {Authorization: `Bearer ${token}`},
    onAfterDone: () => {
      void qc.invalidateQueries({queryKey: ['custom-request', id]});
    },
  });
}
